import { useId, type FC, type SVGProps } from "react";

export const SECONDARY_SNAP_BASE_WIDTH_MM = 70;
export const SECONDARY_SNAP_BASE_THICKNESS_MM = 4;
export const SECONDARY_SNAP_HEAD_SIZE_MM = 7;
export const SECONDARY_SNAP_TOTAL_HEIGHT_MM = SECONDARY_SNAP_HEAD_SIZE_MM + SECONDARY_SNAP_BASE_THICKNESS_MM;

const FILL_COLOR = "#555555";
const STROKE_COLOR = "#000000";
const STROKE_WIDTH = 0.16;

const SecondarySnap: FC<SVGProps<SVGSVGElement>> = (props) => {
  const maskId = useId().replaceAll(":", "");
  const baseY = SECONDARY_SNAP_HEAD_SIZE_MM;
  const baseBottomY = SECONDARY_SNAP_TOTAL_HEIGHT_MM;
  const headX = (SECONDARY_SNAP_BASE_WIDTH_MM - SECONDARY_SNAP_HEAD_SIZE_MM) / 2;
  const headRightX = headX + SECONDARY_SNAP_HEAD_SIZE_MM;
  const baseEndInset = 1.4;
  const baseChamferHeight = 1;
  const notchWidth = 3.2;
  const notchHeight = 1.05;
  const notchY = baseBottomY - notchHeight;
  const baseTopLineY = baseY + 0.75;
  const baseBottomLineY = baseBottomY - 0.75;
  const headLowerLineY = baseY - 1.25;
  const triangleDistanceFromHead = 2;
  const triangleBaseWidth = 3;
  const triangleHeight = 2.1;
  const leftTriangleRightAngleX = headX - triangleDistanceFromHead;
  const rightTriangleRightAngleX = headRightX + triangleDistanceFromHead;
  const leftNotchCenters = [11.2, 18.6];
  const rightNotchCenters = leftNotchCenters.map((center) => SECONDARY_SNAP_BASE_WIDTH_MM - center);
  const basePath = `M ${baseEndInset} ${baseY}
    H ${SECONDARY_SNAP_BASE_WIDTH_MM - baseEndInset}
    L ${SECONDARY_SNAP_BASE_WIDTH_MM} ${baseY + baseChamferHeight}
    V ${baseBottomY - baseChamferHeight}
    L ${SECONDARY_SNAP_BASE_WIDTH_MM - baseEndInset} ${baseBottomY}
    H ${baseEndInset}
    L 0 ${baseBottomY - baseChamferHeight}
    V ${baseY + baseChamferHeight}
    Z`;
  const baseLinePath = `M 0 ${baseTopLineY}
    H ${SECONDARY_SNAP_BASE_WIDTH_MM}
    M 0 ${baseBottomLineY}
    H ${SECONDARY_SNAP_BASE_WIDTH_MM}`;
  const triangleCutoutPath = `M ${leftTriangleRightAngleX} ${baseY}
    H ${leftTriangleRightAngleX - triangleBaseWidth}
    L ${leftTriangleRightAngleX} ${baseY + triangleHeight}
    Z
    M ${rightTriangleRightAngleX} ${baseY}
    H ${rightTriangleRightAngleX + triangleBaseWidth}
    L ${rightTriangleRightAngleX} ${baseY + triangleHeight}
    Z`;
  const triangleCutoutOutlinePath = `M ${leftTriangleRightAngleX} ${baseY}
    L ${leftTriangleRightAngleX} ${baseY + triangleHeight}
    L ${leftTriangleRightAngleX - triangleBaseWidth} ${baseY}
    M ${rightTriangleRightAngleX} ${baseY}
    L ${rightTriangleRightAngleX} ${baseY + triangleHeight}
    L ${rightTriangleRightAngleX + triangleBaseWidth} ${baseY}`;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${SECONDARY_SNAP_BASE_WIDTH_MM} ${SECONDARY_SNAP_TOTAL_HEIGHT_MM}`}
      aria-hidden="true"
      overflow="visible"
      fillRule="evenodd"
      shapeRendering="geometricPrecision"
      {...props}
    >
      <defs>
        <mask
          id={maskId}
          x="0"
          y={baseY}
          width={SECONDARY_SNAP_BASE_WIDTH_MM}
          height={SECONDARY_SNAP_BASE_THICKNESS_MM}
          maskUnits="userSpaceOnUse"
        >
          <rect
            x="0"
            y="0"
            width={SECONDARY_SNAP_BASE_WIDTH_MM}
            height={SECONDARY_SNAP_TOTAL_HEIGHT_MM}
            fill="#ffffff"
          />
          <path d={triangleCutoutPath} fill="#000000" />
          {[...leftNotchCenters, ...rightNotchCenters].map((center) => (
            <rect
              key={center}
              x={center - notchWidth / 2}
              y={notchY}
              width={notchWidth}
              height={notchHeight}
              rx="0.18"
              fill="#000000"
            />
          ))}
        </mask>
      </defs>
      <g mask={`url(#${maskId})`}>
        <path
          d={basePath}
          fill={FILL_COLOR}
          stroke={STROKE_COLOR}
          strokeLinejoin="round"
          strokeWidth={STROKE_WIDTH}
        />
        <path d={baseLinePath} fill="none" stroke={STROKE_COLOR} strokeWidth={STROKE_WIDTH * 0.75} />
      </g>
      <path
        d={`M ${headX} 0
          H ${headX + SECONDARY_SNAP_HEAD_SIZE_MM}
          V ${baseY}
          H ${headX}
          Z`}
        fill={FILL_COLOR}
        stroke={STROKE_COLOR}
        strokeLinejoin="miter"
        strokeWidth={STROKE_WIDTH}
      />
      <path
        d={`M ${headX - 1.8} ${baseY}
          H ${headX}
          M ${headX + SECONDARY_SNAP_HEAD_SIZE_MM} ${baseY}
          H ${headX + SECONDARY_SNAP_HEAD_SIZE_MM + 1.8}
          M ${headX + 0.7} ${headLowerLineY}
          H ${headX + SECONDARY_SNAP_HEAD_SIZE_MM - 0.7}`}
        fill="none"
        stroke={STROKE_COLOR}
        strokeWidth={STROKE_WIDTH * 0.75}
      />
      <path
        d={triangleCutoutOutlinePath}
        fill="none"
        stroke={STROKE_COLOR}
        strokeLinejoin="round"
        strokeWidth={STROKE_WIDTH * 0.85}
      />
      {[...leftNotchCenters, ...rightNotchCenters].map((center) => (
        <path
          key={center}
          d={`M ${center - notchWidth / 2} ${baseBottomY}
            V ${notchY + 0.18}
            Q ${center - notchWidth / 2} ${notchY} ${center - notchWidth / 2 + 0.18} ${notchY}
            H ${center + notchWidth / 2 - 0.18}
            Q ${center + notchWidth / 2} ${notchY} ${center + notchWidth / 2} ${notchY + 0.18}
            V ${baseBottomY}`}
          stroke={STROKE_COLOR}
          fill="none"
          strokeWidth={STROKE_WIDTH * 0.85}
        />
      ))}
    </svg>
  );
};

export default SecondarySnap;
